import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ShieldIcon, LockIcon, EyeIcon, FileTextIcon } from 'lucide-react';
export function PrivacyPolicy() {
  const lastUpdated = 'September 15, 2023';
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1879" data-mp="qd5v934o1rnw">
      <Header data-id="element-1880" data-mp="maj8vhqrkyy3" />
      <main className="flex-grow" data-id="element-1881" data-mp="mb2fw3evm2xc">
        {/* Hero Section */}
        <section className="bg-navy-800 text-white py-12" data-id="element-1882" data-mp="1j0a6vqy9l48n">
          <div className="container mx-auto px-4" data-id="element-1883" data-mp="1j1v1fgkkwn6i">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1884" data-mp="1e1p1ld45nudc">
              <h1 className="text-4xl font-bold mb-4" data-id="element-1885" data-mp="199xkdt6w30zv">Privacy Policy</h1>
              <p className="text-lg mb-2" data-id="element-1886" data-mp="1984a83x2hyn4">
                How we collect, use, and protect your information
              </p>
              <p className="text-sm text-gray-300" data-id="element-1887" data-mp="21q8bfx2usf7j">
                Last Updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>
        {/* Privacy Policy Content */}
        <section className="py-12" data-id="element-1888" data-mp="51177baqqc4d">
          <div className="container mx-auto px-4" data-id="element-1889" data-mp="522gpbc3yqjz">
            <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-1890" data-mp="a04aaq75u89p">
              <div className="p-8" data-id="element-1891" data-mp="a1yjj792kbxy">
                <div className="prose max-w-none" data-id="element-1892" data-mp="3rhdoqdb4dte3">
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1893" data-mp="3rej5gp3ueem9">
                    Introduction
                  </h2>
                  <p className="mb-6" data-id="element-1894" data-mp="3ig753vd6c2gm">
                    The California Institute for Microbiome Research ("CalMiR,"
                    "we," "our," or "us") is committed to protecting your
                    privacy. This Privacy Policy explains how we collect, use,
                    disclose, and safeguard your information when you visit our
                    website, participate in our research, or interact with us in
                    any way.
                  </p>
                  <p className="mb-6" data-id="element-1895" data-mp="1bphggufmt3m7">
                    Please read this Privacy Policy carefully. By accessing or
                    using our website or services, you acknowledge that you have
                    read, understood, and agree to be bound by all the terms
                    outlined in this Privacy Policy.
                  </p>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1896" data-mp="10awazt0bvwky">
                    Information We Collect
                  </h2>
                  <p className="mb-4" data-id="element-1897" data-mp="wavxqboytrfh">
                    We may collect several types of information from and about
                    users of our website and services:
                  </p>
                  <ul className="list-disc pl-6 mb-6" data-id="element-1898" data-mp="1o21oi1isqcs3">
                    <li className="mb-2" data-id="element-1899" data-mp="1j7xibkhcu8s2">
                      <strong data-id="element-1900" data-mp="1j6mddhc6auzd">Personal Information:</strong> This includes
                      information that can identify you as an individual, such
                      as your name, email address, postal address, phone number,
                      and other identifiers.
                    </li>
                    <li className="mb-2" data-id="element-1901" data-mp="26wqe9gxz8gmo">
                      <strong data-id="element-1902" data-mp="3pm2yjtpe8szk">Research Data:</strong> If you participate in our
                      research studies, we may collect biological samples,
                      health information, survey responses, and other
                      research-related data.
                    </li>
                    <li className="mb-2" data-id="element-1903" data-mp="7fygu02rjmfb">
                      <strong data-id="element-1904" data-mp="7gzu8b794l07">Usage Information:</strong> We collect information
                      about your interactions with our website, including IP
                      addresses, browser type, pages visited, and time spent on
                      pages.
                    </li>
                    <li className="mb-2" data-id="element-1905" data-mp="m54az9mqumgc">
                      <strong data-id="element-1906" data-mp="q5y2se3b4lei">Cookies and Similar Technologies:</strong> We use
                      cookies and similar technologies to enhance your
                      experience, gather information about users and their usage
                      patterns, and manage and improve our services.
                    </li>
                  </ul>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1907" data-mp="mgc08r9p0z85">
                    How We Use Your Information
                  </h2>
                  <p className="mb-4" data-id="element-1908" data-mp="w7gfzt770o41">
                    We may use the information we collect for various purposes,
                    including:
                  </p>
                  <ul className="list-disc pl-6 mb-6" data-id="element-1909" data-mp="r954zf55w7k8">
                    <li className="mb-2" data-id="element-1910" data-mp="3hzhqeww6czw">
                      Conducting and advancing scientific research on the
                      microbiome
                    </li>
                    <li className="mb-2" data-id="element-1911" data-mp="dgyo1qu6pwjq">
                      Providing, maintaining, and improving our website and
                      services
                    </li>
                    <li className="mb-2" data-id="element-1912" data-mp="8jymnt8a5hyy">
                      Communicating with you about our research, events, and
                      educational content
                    </li>
                    <li className="mb-2" data-id="element-1913" data-mp="1jwr9qqq4li8o">
                      Processing donations and supporting our fundraising
                      efforts
                    </li>
                    <li className="mb-2" data-id="element-1914" data-mp="1wgo7syze1los">
                      Complying with legal obligations and protecting our rights
                    </li>
                  </ul>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1915" data-mp="1mh73akvvhdue">
                    Data Protection
                  </h2>
                  <p className="mb-6" data-id="element-1916" data-mp="2a82tjdkok0zj">
                    We implement appropriate technical and organizational
                    measures to protect your personal information against
                    unauthorized access, alteration, disclosure, or destruction.
                    However, no method of transmission over the Internet or
                    electronic storage is 100% secure, and we cannot guarantee
                    absolute security.
                  </p>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1917" data-mp="2xwvrffkv4iws">
                    Data Sharing and Disclosure
                  </h2>
                  <p className="mb-4" data-id="element-1918" data-mp="37rt7dqudlqmj">
                    We may share your information in the following
                    circumstances:
                  </p>
                  <ul className="list-disc pl-6 mb-6" data-id="element-1919" data-mp="2yqv2l6c45f2w">
                    <li className="mb-2" data-id="element-1920" data-mp="2ytqy6o0hnro6">
                      With research collaborators and partners for scientific
                      purposes
                    </li>
                    <li className="mb-2" data-id="element-1921" data-mp="2ov1jf43w2nc5">
                      With service providers who perform functions on our behalf
                    </li>
                    <li className="mb-2" data-id="element-1922" data-mp="2g0wxmz3m423h">
                      When required by law or to protect our rights and safety
                    </li>
                    <li className="mb-2" data-id="element-1923" data-mp="2b46gs9jzh2h0">
                      In connection with a merger, acquisition, or sale of
                      assets
                    </li>
                  </ul>
                  <p className="mb-6" data-id="element-1924" data-mp="214pbvuck2sm3">
                    We will always strive to ensure that your personal
                    information is protected when shared with third parties.
                  </p>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1925" data-mp="1vxh7yjtmsjb3">
                    Your Rights and Choices
                  </h2>
                  <p className="mb-4" data-id="element-1926" data-mp="25x80wh2jmq92">
                    Depending on your location, you may have certain rights
                    regarding your personal information, including:
                  </p>
                  <ul className="list-disc pl-6 mb-6" data-id="element-1927" data-mp="1d8av3dfiqlx7">
                    <li className="mb-2" data-id="element-1928" data-mp="1d93xk27g2p9j">
                      The right to access and receive a copy of your personal
                      information
                    </li>
                    <li className="mb-2" data-id="element-1929" data-mp="1mzf7q6hq1uov">
                      The right to correct inaccurate information
                    </li>
                    <li className="mb-2" data-id="element-1930" data-mp="phf76re6oav8">
                      The right to request deletion of your personal information
                    </li>
                    <li className="mb-2" data-id="element-1931" data-mp="28pug9xok7jkl">
                      The right to restrict or object to processing of your
                      information
                    </li>
                    <li className="mb-2" data-id="element-1932" data-mp="2inib0nm60r2r">
                      The right to withdraw consent at any time
                    </li>
                  </ul>
                  <p className="mb-6" data-id="element-1933" data-mp="2dmkvyjwgcd5h">
                    To exercise these rights, please contact us using the
                    information provided in the "Contact Us" section below.
                  </p>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1934" data-mp="2njza2cuwkpum">
                    Changes to This Privacy Policy
                  </h2>
                  <p className="mb-6" data-id="element-1935" data-mp="2we3xsneqpl5x">
                    We may update this Privacy Policy from time to time to
                    reflect changes in our practices or for other operational,
                    legal, or regulatory reasons. We will notify you of any
                    material changes by posting the new Privacy Policy on this
                    page and updating the "Last Updated" date.
                  </p>
                  <h2 className="text-2xl font-bold text-navy-800 mb-4" data-id="element-1936" data-mp="3tzeys58f7rgr">
                    Contact Us
                  </h2>
                  <p className="mb-6" data-id="element-1937" data-mp="3g2jipstr4zr7">
                    If you have questions or concerns about this Privacy Policy
                    or our data practices, please contact us at:
                  </p>
                  <div className="bg-gray-50 p-4 rounded mb-6" data-id="element-1938" data-mp="3b12k63yqppds">
                    <p className="mb-2" data-id="element-1939" data-mp="3b2nj05s37uhg">
                      <strong data-id="element-1940" data-mp="2x9xspp5j03ej">
                        California Institute for Microbiome Research
                      </strong>
                    </p>
                    <p className="mb-2" data-id="element-1941" data-mp="329acg113oucw">123 Science Way</p>
                    <p className="mb-2" data-id="element-1942" data-mp="2nazyq6pbzho5">San Francisco, CA 94107</p>
                    <p className="mb-2" data-id="element-1943" data-mp="2nduhltsp4ufg">Email: privacy@calmir.org</p>
                    <p data-id="element-1944" data-mp="2sa33rr5b9nw4">Phone: (415) 555-0123</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Privacy Commitment Section */}
        <section className="py-12 bg-gray-50" data-id="element-1945" data-mp="3f0e94v5ykaak">
          <div className="container mx-auto px-4" data-id="element-1946" data-mp="301sr5e8hjik1">
            <div className="max-w-4xl mx-auto" data-id="element-1947" data-mp="303dikuzi6ldo">
              <h2 className="text-2xl font-bold text-navy-800 mb-8 text-center" data-id="element-1948" data-mp="3503xhezknaxi">
                Our Commitment to Privacy
              </h2>
              <div className="grid md:grid-cols-3 gap-8" data-id="element-1949" data-mp="3sqq4r41r4r6g">
                <div className="bg-white p-6 rounded-lg shadow-sm text-center" data-id="element-1950" data-mp="1kgbqp6t422r">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1951" data-mp="3j0ep4k8pgtjq">
                    <ShieldIcon size={24} className="text-teal-600" data-id="element-1952" data-mp="3nyzpcjnyjd86" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-700 mb-2" data-id="element-1953" data-mp="fjy3ndfefvgc">
                    Data Protection
                  </h3>
                  <p className="text-gray-600" data-id="element-1954" data-mp="kb6hclnri2zb">
                    We implement strong security measures to protect your
                    personal information.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm text-center" data-id="element-1955" data-mp="200xin53jebqe">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1956" data-mp="2epldf95k6hfa">
                    <LockIcon size={24} className="text-teal-600" data-id="element-1957" data-mp="29s20qdoiecpg" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-700 mb-2" data-id="element-1958" data-mp="1m2quwhsaxs4g">
                    Transparency
                  </h3>
                  <p className="text-gray-600" data-id="element-1959" data-mp="1h4z5buf4qi3f">
                    We're clear about what data we collect and how we use it.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm text-center" data-id="element-1960" data-mp="1r096yh1m8ngo">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1961" data-mp="36h1bbzckfqkt">
                    <EyeIcon size={24} className="text-teal-600" data-id="element-1962" data-mp="32ffopma8bxxf" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-700 mb-2" data-id="element-1963" data-mp="1z48mnxbjrkux">
                    Control
                  </h3>
                  <p className="text-gray-600" data-id="element-1964" data-mp="242tn9xupo6jw">
                    We provide options for you to control your personal
                    information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-1965" data-mp="1gfv7z3ibv4nk" />
    </div>;
}